// Copyright 2025, University of Maryland, All Rights Reserved
import { getWorkspacesApi } from './api';
import type { WorkspaceInfo, WorkspaceResponse } from '../types/workspace';
import type { UserProfileResponse } from '../types/user';


export type WorkspaceApi = ReturnType<typeof useWorkspaceApi>;

// Hook-based API for internal use
export const useWorkspaceApi = () => {
  const apiInstance = getWorkspacesApi();

  return {
    // Regular user methods
    fetchOrgWorkspaces: async (org_id: string): Promise<WorkspaceInfo[]> => {
      if (!org_id) {
        throw new Error('Organization ID is required');
      }

      console.log('[Workspaces] Fetching accessible workspaces for org:', org_id);
      try {
        const response = await apiInstance.get<WorkspaceInfo[]>(`/workspaces/${org_id}`);
        console.log('[Workspaces] Fetched workspaces:', response.data.length);
        return response.data;
      } catch (error) {
        console.error('[Workspaces] Error fetching workspaces:', error);
        throw error;
      }
    },

    getWorkspace: async (org_id: string, ws_id: string): Promise<WorkspaceResponse> => {
      console.log('[Workspaces] Fetching workspace:', org_id, ws_id);
      const response = await apiInstance.get<WorkspaceResponse>(`/workspaces/${org_id}/${ws_id}`);
      console.log('[Workspaces] Fetched workspace:', response.data);
      return response.data;
    },

    // Admin methods
    adminListWorkspaces: async (org_id: string): Promise<WorkspaceResponse[]> => {
      console.log('[Workspaces] Admin: Fetching workspaces for organization:', org_id);
      const response = await apiInstance.get<WorkspaceResponse[]>(`/workspaces/${org_id}`);
      console.log('[Workspaces] Admin: Fetched workspaces:', response.data);
      return response.data;
    },

    adminCreateWorkspace: async (
      org_id: string,
      data: { ws_id: string; name: string; description?: string }
    ): Promise<WorkspaceResponse> => {
      console.log('[Workspaces] Admin: Creating workspace for organization:', org_id, data);
      const response = await apiInstance.post<WorkspaceResponse>(
        `/admin/workspaces/${org_id}`,
        data
      );
      console.log('[Workspaces] Admin: Created workspace:', response.data);
      return response.data;
    },

    adminGetWorkspace: async (org_id: string, ws_id: string): Promise<WorkspaceResponse> => {
      console.log('[Workspaces] Admin: Fetching workspace:', org_id, ws_id);
      const response = await apiInstance.get<WorkspaceResponse>(`/workspaces/${org_id}/${ws_id}`);
      console.log('[Workspaces] Admin: Fetched workspace:', response.data);
      return response.data;
    },

    adminUpdateWorkspace: async (
      org_id: string,
      ws_id: string,
      data: { name: string; description?: string }
    ): Promise<WorkspaceResponse> => {
      console.log('[Workspaces] Admin: Updating workspace:', org_id, ws_id, data);
      const response = await apiInstance.put<WorkspaceResponse>(
        `/admin/workspaces/${org_id}/${ws_id}`,
        data
      );
      console.log('[Workspaces] Admin: Updated workspace:', response.data);
      return response.data;
    },

    adminDeleteWorkspace: async (org_id: string, ws_id: string): Promise<void> => {
      console.log('[Workspaces] Admin: Deleting workspace:', org_id, ws_id);
      await apiInstance.delete(`/admin/workspaces/${org_id}/${ws_id}?confirm=true`);
      console.log('[Workspaces] Admin: Deleted workspace');
    },

    getWorkspaceUsers: async (org_id: string, ws_id: string): Promise<UserProfileResponse[]> => {
      console.log('[Workspaces] Fetching users for workspace:', { org_id, ws_id });
      const response = await apiInstance.get<UserProfileResponse[]>(
        `/admin/organizations/${org_id}/workspaces/${ws_id}/users`
      );
      console.log('[Workspaces] Fetched workspace users:', response.data);
      return response.data;
    }
  };
};

// Standalone API functions for use outside React components
export const fetchOrgWorkspaces = async (org_id: string): Promise<WorkspaceInfo[]> => {
  if (!org_id) {
    throw new Error('Organization ID is required');
  }

  const apiInstance = getWorkspacesApi();
  try {
    const response = await apiInstance.get<WorkspaceInfo[]>(`/workspaces/${org_id}`);
    return response.data;
  } catch (error) {
    console.error('[Workspaces] Error fetching workspaces:', error);
    throw error;
  }
};
